import { useRef, useEffect } from "react";
import { Clock, ChevronUp, ChevronDown } from "lucide-react";

/**
 * TimePicker - selector de hora moderno y responsive.
 *
 * Implementación con botones +/- (sin Portals/Selects/Dropdowns) para que
 * funcione fluido dentro de Dialogs y en cualquier dispositivo (mobile, tablet, desktop).
 *
 * Controles:
 *  - Click/Touch sobre las flechas para subir/bajar
 *  - En desktop: rueda del ratón (scroll) sobre el número
 *  - Soporta auto-incremento al mantener pulsado
 *
 * Props:
 *  - label: string mostrado encima
 *  - value: "HH:MM"
 *  - onChange: (newValue: "HH:MM") => void
 *  - accent: "violet" | "purple"
 *  - testId?: string
 *  - minuteStep?: number (default 15)
 */
export default function TimePicker({
  label,
  value = "09:00",
  onChange,
  accent = "violet",
  testId,
  minuteStep = 15,
}) {
  const [hStr, mStr] = (value || "00:00").split(":");
  const h = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;

  const accentClasses = {
    violet: {
      icon: "text-violet-400",
      iconBg: "bg-violet-500/10",
      display: "from-violet-500/15 to-purple-500/10 border-violet-500/25",
      btn: "hover:bg-violet-500/20 active:bg-violet-500/30",
      btnIcon: "text-violet-300",
    },
    purple: {
      icon: "text-purple-400",
      iconBg: "bg-purple-500/10",
      display: "from-purple-500/15 to-fuchsia-500/10 border-purple-500/25",
      btn: "hover:bg-purple-500/20 active:bg-purple-500/30",
      btnIcon: "text-purple-300",
    },
  };
  const a = accentClasses[accent] || accentClasses.violet;

  const fmt = (n) => String(n).padStart(2, "0");

  const updateHour = (newH) => {
    const wrapped = ((newH % 24) + 24) % 24;
    onChange?.(`${fmt(wrapped)}:${fmt(m)}`);
  };

  const updateMinute = (newM) => {
    // Avanza al siguiente múltiplo de minuteStep y permite wrap a cambio de hora
    const total = h * 60 + newM;
    const wrappedTotal = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
    const newH = Math.floor(wrappedTotal / 60);
    const newMin = wrappedTotal % 60;
    onChange?.(`${fmt(newH)}:${fmt(newMin)}`);
  };

  // Hold-to-repeat: cuando mantienes pulsado el botón, incrementa cada 120ms
  const useHoldRepeat = (fn) => {
    const timerRef = useRef(null);
    const intervalRef = useRef(null);
    const start = () => {
      fn();
      timerRef.current = setTimeout(() => {
        intervalRef.current = setInterval(fn, 100);
      }, 400);
    };
    const stop = () => {
      clearTimeout(timerRef.current);
      clearInterval(intervalRef.current);
      timerRef.current = null;
      intervalRef.current = null;
    };
    useEffect(() => stop, []);
    return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop, onTouchStart: start, onTouchEnd: stop, onTouchCancel: stop };
  };

  const hourUp = useHoldRepeat(() => updateHour(h + 1));
  const hourDown = useHoldRepeat(() => updateHour(h - 1));
  const minUp = useHoldRepeat(() => updateMinute(m + minuteStep));
  const minDown = useHoldRepeat(() => updateMinute(m - minuteStep));

  // Scroll wheel support
  const handleWheel = (e, type) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    if (type === "hour") updateHour(h + dir);
    else updateMinute(m + dir * minuteStep);
  };

  return (
    <div className={`rounded-2xl p-4 sm:p-5 bg-gradient-to-br ${a.display} border backdrop-blur-sm`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${a.iconBg} flex items-center justify-center ${a.icon}`}>
          <Clock className="w-4 h-4" />
        </div>
        <span className="text-gray-200 text-sm font-medium">{label}</span>
      </div>

      {/* Time controls — botones +/- arriba/abajo de cada número */}
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        {/* Hour */}
        <TimeColumn
          label="Hora"
          value={fmt(h)}
          onUp={hourUp}
          onDown={hourDown}
          onWheel={(e) => handleWheel(e, "hour")}
          accent={a}
          testId={testId ? `${testId}-hour` : undefined}
        />

        {/* Separator */}
        <div className="text-3xl sm:text-4xl font-bold text-gray-500 pb-6 select-none">:</div>

        {/* Minute */}
        <TimeColumn
          label="Minutos"
          value={fmt(m)}
          onUp={minUp}
          onDown={minDown}
          onWheel={(e) => handleWheel(e, "minute")}
          accent={a}
          testId={testId ? `${testId}-minute` : undefined}
        />
      </div>

      <p className="text-[10px] text-gray-500 text-center mt-3 select-none">
        Toca las flechas o desliza la rueda del ratón
      </p>
    </div>
  );
}

function TimeColumn({ label, value, onUp, onDown, onWheel, accent, testId }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        aria-label={`Aumentar ${label.toLowerCase()}`}
        className={`w-12 h-9 sm:w-14 sm:h-10 rounded-lg flex items-center justify-center bg-white/5 ${accent.btn} transition-colors touch-manipulation`}
        {...onUp}
        data-testid={testId ? `${testId}-up` : undefined}
      >
        <ChevronUp className={`w-5 h-5 ${accent.btnIcon}`} />
      </button>

      <div
        className="w-16 sm:w-20 h-14 sm:h-16 bg-[#0d0b12]/70 border border-white/10 rounded-xl flex items-center justify-center cursor-ns-resize select-none"
        onWheel={onWheel}
        data-testid={testId ? `${testId}-value` : undefined}
      >
        <span className="text-3xl sm:text-4xl font-bold text-white tabular-nums tracking-tight">
          {value}
        </span>
      </div>

      <button
        type="button"
        aria-label={`Disminuir ${label.toLowerCase()}`}
        className={`w-12 h-9 sm:w-14 sm:h-10 rounded-lg flex items-center justify-center bg-white/5 ${accent.btn} transition-colors touch-manipulation`}
        {...onDown}
        data-testid={testId ? `${testId}-down` : undefined}
      >
        <ChevronDown className={`w-5 h-5 ${accent.btnIcon}`} />
      </button>

      <span className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5 select-none">{label}</span>
    </div>
  );
}
